import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import StarRating from '@/components/ui/star-rating'

import {
    useSubmitTicketRating,
    useTicketRatingView,
} from '../hooks/use-internal-rating'

// Khớp lowRatingThreshold backend (ratings.controllers.go): <=2 sao bắt buộc nêu lý do.
const LOW_RATING_THRESHOLD = 2

interface Props {
    ticketId: number
    open: boolean
    onClose: () => void
}

export const InternalRatingDialog = ({ ticketId, open, onClose }: Props) => {
    const { t } = useTranslation('rating')
    const [rating, setRating] = useState(0)
    const [comment, setComment] = useState('')

    const {
        data: view,
        renderState,
        isLoading,
    } = useTicketRatingView(ticketId, open)
    const { mutateAsync, isPending } = useSubmitTicketRating(ticketId)

    // Reset mỗi lần mở dialog / đổi ticket.
    useEffect(() => {
        if (open) {
            setRating(0)
            setComment('')
        }
    }, [open, ticketId])

    const context = view?.context
    const eligible = context?.eligible
    const alreadyRated = !!view?.rating || !!context?.already_rated
    const needComment = rating > 0 && rating <= LOW_RATING_THRESHOLD
    const canSubmit =
        !!eligible &&
        !alreadyRated &&
        rating >= 1 &&
        !(needComment && !comment.trim())

    const handleSubmit = async () => {
        if (!canSubmit) return
        const res = await mutateAsync({ rating, comment: comment.trim() })
        if (res) onClose()
    }

    const stateNode = renderState()

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{t('dialog.title')}</DialogTitle>
                    <DialogDescription>
                        {t('dialog.subtitle', { ticket: ticketId })}
                    </DialogDescription>
                </DialogHeader>

                {stateNode ? (
                    stateNode
                ) : !eligible ? (
                    <p className="py-4 text-sm text-gray-500">
                        {t('dialog.not_eligible')}
                    </p>
                ) : alreadyRated ? (
                    <div className="space-y-2 py-4">
                        <p className="text-sm text-gray-600">
                            {t('dialog.already_rated')}
                        </p>
                        {view?.rating && (
                            <StarRating value={view.rating.rating} readOnly />
                        )}
                        {view?.rating?.comment && (
                            <p className="text-sm italic text-gray-500">
                                “{view.rating.comment}”
                            </p>
                        )}
                    </div>
                ) : (
                    <div className="space-y-4 py-2">
                        {/* W-UI3: TÊN NGƯỜI là thông tin chính. Tập người bị chấm gồm
                            approved_by ∪ technical ∪ cs ∪ sale ∪ employee − người chấm,
                            rộng hơn ô "phụ trách kỹ thuật" trên màn ticket. Trước đây
                            dialog chỉ hiện tên phòng ban nên người chấm không biết mình
                            đang chấm ai (ticket 688: 2 người khác nhau cùng phòng
                            TECHNOLOGY -> chỉ thấy 1 dòng "TECHNOLOGY"). */}
                        {!!context?.rated_employee_names?.length && (
                            <div className="rounded-md border border-gray-100 bg-gray-50/60 p-2.5">
                                <p className="text-sm text-gray-500">
                                    {t('dialog.rating_for')}
                                </p>
                                <p className="mt-0.5 text-sm font-medium text-gray-800">
                                    {context.rated_employee_names.join(', ')}
                                </p>
                                {!!context.rated_departments?.length && (
                                    <div className="mt-1.5 flex flex-wrap gap-1">
                                        {context.rated_departments.map((d) => (
                                            <Badge key={d} variant="secondary">
                                                {d}
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* BE cũ (chưa deploy W-UI3) không trả rated_employee_names ->
                            rơi về bản chỉ có phòng ban như trước, không để trống. */}
                        {!context?.rated_employee_names?.length &&
                            !!context?.rated_departments?.length && (
                                <div className="flex flex-wrap items-center gap-1.5">
                                    <span className="text-sm text-gray-500">
                                        {t('dialog.rating_for')}
                                    </span>
                                    {context.rated_departments.map((d) => (
                                        <Badge key={d} variant="secondary">
                                            {d}
                                        </Badge>
                                    ))}
                                </div>
                            )}
                        <div className="flex flex-col items-center gap-2 py-2">
                            <StarRating
                                value={rating}
                                onChange={setRating}
                                size="lg"
                            />
                            <span className="text-xs text-gray-400">
                                {t('dialog.tap_to_rate')}
                            </span>
                        </div>
                        <Textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            rows={3}
                            placeholder={
                                needComment
                                    ? t('dialog.comment_required')
                                    : t('dialog.comment_optional')
                            }
                        />
                        {needComment && !comment.trim() && (
                            <p className="text-xs text-red-500">
                                {t('dialog.comment_required_hint')}
                            </p>
                        )}
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        {t('dialog.close')}
                    </Button>
                    {eligible && !alreadyRated && (
                        <Button
                            onClick={handleSubmit}
                            disabled={!canSubmit || isPending || isLoading}
                        >
                            {t('dialog.submit')}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

export default InternalRatingDialog
